import React, { useEffect, useState } from 'react';
import { Cookie, ShieldCheck, ShieldX, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { consentApi } from '../../lib/api';

const CONSENT_KEY = 'tracking_consent';
const CLIENT_ID_KEY = 'tracking_client_id';

function readConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) || '';
  } catch (_e) {
    return '';
  }
}

function readClientId() {
  try {
    return localStorage.getItem(CLIENT_ID_KEY) || '';
  } catch (_e) {
    return '';
  }
}

function fireConsentChange(value) {
  try {
    if (value) localStorage.setItem(CONSENT_KEY, value);
    else localStorage.removeItem(CONSENT_KEY);
  } catch (_e) {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent('tracking:consent', { detail: value || '' }));
}

export function CookieConsentCard() {
  const [consent, setConsent] = useState(() => readConsent());
  const [clientId, setClientId] = useState(() => readClientId());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onChange = (e) => {
      setConsent(e.detail || '');
      setClientId(readClientId());
    };
    window.addEventListener('tracking:consent', onChange);
    return () => window.removeEventListener('tracking:consent', onChange);
  }, []);

  const logEvent = async (choice) => {
    try {
      await consentApi.log({
        choice,
        clientId: clientId || readClientId(),
        pageUrl: window.location.href,
      });
    } catch (_e) {
      /* non-fatal */
    }
  };

  const onAccept = async () => {
    setBusy(true);
    try {
      fireConsentChange('accepted');
      setConsent('accepted');
      await logEvent('accepted');
      toast.success('Analytics tracking enabled.');
    } finally {
      setBusy(false);
    }
  };

  const onReject = async () => {
    setBusy(true);
    try {
      fireConsentChange('rejected');
      setConsent('rejected');
      await logEvent('rejected');
      toast.success('Analytics tracking blocked on this device.');
    } finally {
      setBusy(false);
    }
  };

  const onReopen = async () => {
    setBusy(true);
    try {
      if (consent === 'accepted') await logEvent('withdrawn');
      fireConsentChange('');
      setConsent('');
      toast.message('Consent reset — the banner will reappear.');
    } finally {
      setBusy(false);
    }
  };

  const statusBadge =
    consent === 'accepted' ? (
      <Badge className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30 font-mono-edge text-[10px]">
        <ShieldCheck size={10} className="mr-1" /> Accepted
      </Badge>
    ) : consent === 'rejected' ? (
      <Badge className="bg-destructive/15 text-destructive border border-destructive/30 font-mono-edge text-[10px]">
        <ShieldX size={10} className="mr-1" /> Rejected
      </Badge>
    ) : (
      <Badge className="bg-muted text-muted-foreground border border-border font-mono-edge text-[10px]">
        Not yet decided
      </Badge>
    );

  return (
    <Card className="border-border" data-testid="cookie-consent-card">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Cookie size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-semibold">Cookie & tracking preferences</h2>
              {statusBadge}
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Control whether Blayzrr loads analytics and marketing tags on this device.
              Your decision (and any change to it) is recorded in our compliance log under an
              anonymous client identifier — never tied to personal data beyond your account
              email.
            </p>
            {clientId && (
              <p className="text-[10px] text-muted-foreground mt-2 font-mono-edge truncate" title={clientId}>
                Device ID: {clientId}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={onAccept}
                disabled={busy || consent === 'accepted'}
                data-testid="settings-cookie-accept"
                className="rounded-xl"
              >
                <ShieldCheck size={12} className="mr-1.5" /> Accept tracking
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onReject}
                disabled={busy || consent === 'rejected'}
                data-testid="settings-cookie-reject"
                className="rounded-xl"
              >
                <ShieldX size={12} className="mr-1.5" /> Reject tracking
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onReopen}
                disabled={busy || !consent}
                data-testid="settings-cookie-reopen"
                className="rounded-xl"
              >
                <RotateCcw size={12} className="mr-1.5" /> Reset choice
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default CookieConsentCard;
